import { create } from 'zustand'
import type {
  Deal,
  DealStage,
  MainTab,
  ViewMode,
  DealPlan,
  DealService,
  DealPartner,
  GoalTarget,
} from '@/types'
import { filterVisibleDeals } from '@/components/Deals/filterVisibleDeals'
import {
  appendDealIfMissing,
  mergeDealsPages,
  patchDealInState,
  removeDealFromState,
} from '@/stores/dealStoreMutations'

const VALID_VIEW_MODES: ViewMode[] = ['journey', 'kanban', 'list']

function readDealsViewMode(): ViewMode {
  const stored = localStorage.getItem('deals_view_mode')
  if (stored && VALID_VIEW_MODES.includes(stored as ViewMode)) {
    return stored as ViewMode
  }
  return 'journey'
}

interface DealsState {
  // Data
  deals: Deal[]
  selectedDeal: Deal | null
  loading: boolean

  // View
  viewMode: ViewMode
  mainTab: MainTab

  // Filters
  closerFilter: string
  searchTerm: string
  showStagnantOnly: boolean
  etapaFilter: string
  periodFilter: string

  // Supporting data
  plans: DealPlan[]
  services: DealService[]
  partners: DealPartner[]
  goalTargets: GoalTarget

  // Initial tab when opening detail panel
  initialTab: string

  /** Campos a destacar no painel do deal (ex.: dataReuniao ao falhar transição). */
  dealPanelHighlight: { dealId: string; fields: string[] } | null

  /** Painel lateral ou modal de contrato abertos — oculta FAB de blocos. */
  dealDetailUiOpen: boolean
  dealContractModalOpen: boolean

  // Actions
  setDeals: (deals: Deal[]) => void
  setSelectedDeal: (deal: Deal | null) => void
  setLoading: (loading: boolean) => void
  setViewMode: (mode: ViewMode) => void
  setMainTab: (tab: MainTab) => void
  setCloserFilter: (filter: string) => void
  setSearchTerm: (term: string) => void
  setShowStagnantOnly: (show: boolean) => void
  setEtapaFilter: (filter: string) => void
  setPeriodFilter: (filter: string) => void
  setPlans: (plans: DealPlan[]) => void
  setServices: (services: DealService[]) => void
  setPartners: (partners: DealPartner[]) => void
  setGoalTargets: (targets: GoalTarget) => void
  setInitialTab: (tab: string) => void
  setDealPanelHighlight: (highlight: { dealId: string; fields: string[] } | null) => void
  clearDealPanelHighlight: (dealId?: string) => void
  setDealDetailUiOpen: (open: boolean) => void
  setDealContractModalOpen: (open: boolean) => void
  updateDealById: (id: string, updates: Partial<Deal> | Deal) => void
  removeDealById: (id: string) => void
  upsertDeal: (deal: Deal) => void
  appendDealsPage: (deals: Deal[]) => void

  // Computed helpers
  getVisibleDeals: (userRole?: string, userId?: string) => Deal[]
  getDealsByStage: (stage: DealStage) => Deal[]
}

export const useDealsStore = create<DealsState>((set, get) => ({
  // Data
  deals: [],
  selectedDeal: null,
  loading: false,

  // View
  viewMode: readDealsViewMode(),
  mainTab: 'active',

  // Filters
  closerFilter: 'all',
  searchTerm: '',
  showStagnantOnly: false,
  etapaFilter: 'all',
  periodFilter: 'all',

  // Supporting data
  plans: [],
  services: [],
  partners: [],
  goalTargets: {},

  // Initial tab
  initialTab: 'info',

  dealPanelHighlight: null,
  dealDetailUiOpen: false,
  dealContractModalOpen: false,

  // Actions
  setDeals: (deals) => set({ deals }),
  setSelectedDeal: (deal) => set({ selectedDeal: deal }),
  setLoading: (loading) => set({ loading }),
  setViewMode: (mode) => {
    localStorage.setItem('deals_view_mode', mode)
    set({ viewMode: mode })
  },
  setMainTab: (tab) => set({ mainTab: tab }),
  setCloserFilter: (filter) => set({ closerFilter: filter }),
  setSearchTerm: (term) => set({ searchTerm: term }),
  setShowStagnantOnly: (show) => set({ showStagnantOnly: show }),
  setEtapaFilter: (filter) => set({ etapaFilter: filter }),
  setPeriodFilter: (filter) => set({ periodFilter: filter }),
  setPlans: (plans) => set({ plans }),
  setServices: (services) => set({ services }),
  setPartners: (partners) => set({ partners }),
  setGoalTargets: (targets) => set({ goalTargets: targets }),
  setInitialTab: (tab) => set({ initialTab: tab }),

  setDealPanelHighlight: (highlight) => set({ dealPanelHighlight: highlight }),

  clearDealPanelHighlight: (dealId) =>
    set((state) => {
      if (!state.dealPanelHighlight) return state
      if (dealId && state.dealPanelHighlight.dealId !== dealId) return state
      return { dealPanelHighlight: null }
    }),

  setDealDetailUiOpen: (open) => set({ dealDetailUiOpen: open }),
  setDealContractModalOpen: (open) => set({ dealContractModalOpen: open }),

  updateDealById: (id, updates) =>
    set((state) => patchDealInState(state, id, updates)),

  removeDealById: (id) => set((state) => removeDealFromState(state, id)),

  upsertDeal: (deal) => set((state) => appendDealIfMissing(state, deal)),

  appendDealsPage: (incoming) =>
    set((state) => ({ deals: mergeDealsPages(state.deals, incoming) })),

  // Computed helpers
  getVisibleDeals: (userRole, userId) => {
    const state = get()
    return filterVisibleDeals(state.deals, {
      mainTab: state.mainTab,
      closerFilter: state.closerFilter,
      searchTerm: state.searchTerm,
      showStagnantOnly: state.showStagnantOnly,
      etapaFilter: state.etapaFilter,
      periodFilter: state.periodFilter,
      userRole,
      userId,
    })
  },

  getDealsByStage: (stage) => {
    const { deals } = get()
    return deals.filter((d) => d.etapa === stage)
  },
}))
